// src/components/dashboard/groups/GroupsMainTab.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Users, Plus, ChevronRight, Trophy, Star,
    Calendar, Shield, Lock, ExternalLink,
    UserPlus, BarChart2, X
} from 'lucide-react';
import './GroupsMain.css';
import GlobalRankingTab from './GlobalRankingTab';
import { useAuth } from '../../../contexts/AuthContext';
import { useFeatures } from '../../../contexts/FeaturesContext';

const API_BASE_URL = '/api';

interface StudyGroup {
    _id: string;
    name: string;
    description?: string;
    groupIcon?: string;
    inviteCode: string;
    memberCount: number;
    groupStats: {
        totalCardsMastered: number;
        totalCardsStudied: number;
        groupScore?: number;
    };
    members: Array<{
        userId: string | { _id: string };
        role: 'admin' | 'member';
    }>;
}

interface GroupsMainTabProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

// ── Modal de Upgrade ──────────────────────────────────────────
const GroupUpgradeModal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
    <div className="gpmantb-upgrade-overlay" onClick={onClose}>
        <div className="gpmantb-upgrade-card" onClick={e => e.stopPropagation()}>
            <button className="gpmantb-upgrade-close" onClick={onClose}>
                <X size={18} />
            </button>

            <div className="gpmantb-upgrade-icon-wrap">
                <Lock size={30} strokeWidth={1.6} />
            </div>

            <h2 className="gpmantb-upgrade-title">Acesso restrito</h2>

            <p className="gpmantb-upgrade-desc">
                Grupos de estudo estão disponíveis no plano <strong>Acesso Total</strong>.
                Crie grupos, entre em turmas e acompanhe o ranking junto com outros alunos.
            </p>

            <div className="gpmantb-upgrade-features">
                <div className="gpmantb-upgrade-feat">
                    <Users size={14} />
                    <span>Criar e gerenciar grupos</span>
                </div>
                <div className="gpmantb-upgrade-feat">
                    <Trophy size={14} />
                    <span>Ranking global e por grupo</span>
                </div>
                <div className="gpmantb-upgrade-feat">
                    <BarChart2 size={14} />
                    <span>Estatísticas coletivas</span>
                </div>
            </div>

            <div className="gpmantb-upgrade-actions">
                <button
                    className="gpmantb-upgrade-btn-primary"
                    onClick={() => { window.open('/marketplace', '_blank', 'noopener,noreferrer'); onClose(); }}
                >
                    <ExternalLink size={15} />
                    Ver planos
                </button>
                <button className="gpmantb-upgrade-btn-secondary" onClick={onClose}>
                    Agora não
                </button>
            </div>
        </div>
    </div>
);

// ── Componente principal ──────────────────────────────────────
const GroupsMainTab: React.FC<GroupsMainTabProps> = ({ onShowToast }) => {
    const navigate = useNavigate();
    const { user, isAdmin } = useAuth();
    const { canAccess } = useFeatures();
    const hasGroupsAccess = isAdmin || canAccess('groups');

    const [activeTab, setActiveTab] = useState<'my-groups' | 'join' | 'ranking'>('my-groups');
    const [myGroups, setMyGroups] = useState<StudyGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [inviteCode, setInviteCode] = useState('');
    const [joiningGroup, setJoiningGroup] = useState(false);
    const [globalRanking, setGlobalRanking] = useState<any>(null);
    const [groupName, setGroupName] = useState('');
    const [groupDescription, setGroupDescription] = useState('');

    const requireAccess = (action: () => void) => {
        if (!hasGroupsAccess) { setShowUpgradeModal(true); return; }
        action();
    };

    const isGroupAdmin = (group: StudyGroup): boolean => {
        if (!user?.userId) return false;
        return group.members.some(m => {
            const memberId = typeof m.userId === 'string' ? m.userId : (m.userId as any)?._id;
            return memberId === user.userId && m.role === 'admin';
        });
    };

    const loadMyGroups = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/groups/my-groups`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) setMyGroups(await response.json());
        } catch {
            onShowToast('Erro ao carregar grupos', 'error');
        } finally {
            setLoading(false);
        }
    }, [onShowToast]);

    const loadGlobalRanking = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/groups/global/ranking`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) setGlobalRanking(await response.json());
        } catch { /* silencioso */ }
    }, []);

    useEffect(() => {
        loadMyGroups();
        loadGlobalRanking();
    }, [loadMyGroups, loadGlobalRanking]);

    const handleCreateGroup = async () => {
        if (!groupName.trim()) { onShowToast('Nome do grupo é obrigatório', 'error'); return; }
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/groups`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ name: groupName.trim(), description: groupDescription.trim() })
            });
            if (response.ok) {
                onShowToast('Grupo criado com sucesso', 'success');
                setShowCreateModal(false);
                setGroupName('');
                setGroupDescription('');
                loadMyGroups();
            } else {
                const err = await response.json();
                onShowToast(err.message || 'Erro ao criar grupo', 'error');
            }
        } catch {
            onShowToast('Erro ao criar grupo', 'error');
        }
    };

    const handleJoinGroup = async () => {
        if (!inviteCode.trim()) { onShowToast('Digite um código de convite', 'error'); return; }
        setJoiningGroup(true);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/groups/join/${inviteCode.trim().toUpperCase()}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                onShowToast('Você entrou no grupo', 'success');
                setInviteCode('');
                setActiveTab('my-groups');
                loadMyGroups();
            } else {
                const err = await response.json();
                onShowToast(err.message || 'Código inválido', 'error');
            }
        } catch {
            onShowToast('Erro ao entrar no grupo', 'error');
        } finally {
            setJoiningGroup(false);
        }
    };

    if (loading) {
        return (
            <div className="gpmantb-loading">
                <div className="gpmantb-spinner" />
                <p>Carregando grupos...</p>
            </div>
        );
    }

    return (
        <div className="gpmantb-container">

            {/* ── Tabs de navegação ── */}
            <div className="gpmantb-pills">
                <button
                    className={`gpmantb-pill ${activeTab === 'my-groups' ? 'active' : ''}`}
                    onClick={() => setActiveTab('my-groups')}
                >
                    <Users size={14} />
                    Meus Grupos
                    {myGroups.length > 0 && (
                        <span className="gpmantb-pill-count">{myGroups.length}</span>
                    )}
                </button>
                <button
                    className={`gpmantb-pill ${activeTab === 'join' ? 'active' : ''}`}
                    onClick={() => setActiveTab('join')}
                >
                    <UserPlus size={14} />
                    Entrar em Grupo
                </button>
                <button
                    className={`gpmantb-pill ${activeTab === 'ranking' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ranking')}
                >
                    <Trophy size={14} />
                    Ranking Global
                </button>
            </div>

            {/* ── Meus Grupos ── */}
            {activeTab === 'my-groups' && (
                <div className="gpmantb-tab-content">
                    {myGroups.length === 0 ? (
                        <div className="gpmantb-empty">
                            <div className="gpmantb-empty-icon-wrap">
                                <Users size={36} strokeWidth={1.4} />
                            </div>
                            <h3>Você ainda não participa de nenhum grupo</h3>
                            <p>
                                Estudar em grupo aumenta consistência e desempenho.
                                Entre em um grupo ou crie o seu.
                            </p>
                            <div className="gpmantb-empty-actions">
                                <button
                                    className="gpmantb-btn-primary"
                                    onClick={e => { e.preventDefault(); e.stopPropagation(); requireAccess(() => setShowCreateModal(true)); }}
                                >
                                    <Plus size={16} />
                                    Criar Grupo
                                    {!hasGroupsAccess && <Lock size={12} style={{ marginLeft: 4 }} />}
                                </button>
                                <button
                                    className="gpmantb-btn-secondary"
                                    onClick={e => { e.preventDefault(); e.stopPropagation(); requireAccess(() => setActiveTab('join')); }}
                                >
                                    Entrar em Grupo
                                    {!hasGroupsAccess && <Lock size={12} style={{ marginLeft: 4 }} />}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="gpmantb-grid">
                            {myGroups.map((group) => (
                                <div
                                    key={group._id}
                                    className="gpmantb-card"
                                    onClick={() => navigate(`/groups/${group._id}`)}
                                >
                                    {group.groupIcon ? (
                                        <img src={group.groupIcon} alt={group.name} className="gpmantb-card-icon" />
                                    ) : (
                                        <div className="gpmantb-card-icon-placeholder">
                                            <Users size={26} />
                                        </div>
                                    )}

                                    <div className="gpmantb-card-content">
                                        <div className="gpmantb-card-top">
                                            <h3>{group.name}</h3>
                                            {isGroupAdmin(group) && (
                                                <span className="gpmantb-admin-badge">
                                                    <Shield size={10} />
                                                    Admin
                                                </span>
                                            )}
                                        </div>

                                        {group.description && (
                                            <p className="gpmantb-card-desc">{group.description}</p>
                                        )}

                                        <div className="gpmantb-card-stats">
                                            <span className="gpmantb-card-stat">
                                                <Users size={12} />
                                                {group.memberCount ?? group.members.length} membros
                                            </span>
                                            {(group.groupStats?.groupScore ?? 0) > 0 && (
                                                <span className="gpmantb-card-stat">
                                                    <Star size={12} />
                                                    {group.groupStats.groupScore} pts
                                                </span>
                                            )}
                                        </div>

                                        <button
                                            className="gpmantb-btn-view"
                                            onClick={e => { e.stopPropagation(); navigate(`/groups/${group._id}`); }}
                                        >
                                            Ver Grupo
                                            <ChevronRight size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── Entrar em Grupo ── */}
            {activeTab === 'join' && (
                <div className="gpmantb-tab-content">
                    <div className="gpmantb-join-section">
                        <div className="gpmantb-join-card">
                            <div className="gpmantb-join-icon-wrap">
                                <UserPlus size={32} strokeWidth={1.4} />
                            </div>
                            <h3>Entrar em um Grupo</h3>
                            <p>Digite o código de convite compartilhado pelo administrador do grupo</p>

                            <div className="gpmantb-join-form">
                                <input
                                    type="text"
                                    placeholder="ABCD1234"
                                    value={inviteCode}
                                    onChange={e => setInviteCode(e.target.value.toUpperCase())}
                                    maxLength={8}
                                    className="gpmantb-invite-input"
                                />
                                <button
                                    className="gpmantb-btn-join"
                                    onClick={() => requireAccess(handleJoinGroup)}
                                    disabled={joiningGroup || !inviteCode.trim()}
                                >
                                    {!hasGroupsAccess && <Lock size={14} />}
                                    {joiningGroup ? 'Entrando...' : 'Entrar no Grupo'}
                                </button>
                            </div>

                            <div className="gpmantb-join-perks">
                                <div className="gpmantb-perk">
                                    <Trophy size={16} />
                                    <span>Compete com outros membros</span>
                                </div>
                                <div className="gpmantb-perk">
                                    <Star size={16} />
                                    <span>Acompanhe o progresso coletivo</span>
                                </div>
                                <div className="gpmantb-perk">
                                    <Calendar size={16} />
                                    <span>Estude junto e mantenha a consistência</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Ranking Global ── */}
            {activeTab === 'ranking' && (
                <div className="gpmantb-tab-content">
                    <GlobalRankingTab
                        rankingData={globalRanking}
                        isAdmin={isAdmin}
                        onShowToast={onShowToast}
                    />
                </div>
            )}

            {/* ── Modal Criar Grupo ── */}
            {showCreateModal && (
                <div className="gpmantb-dialog-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="gpmantb-dialog" onClick={e => e.stopPropagation()}>
                        <h3>Criar Grupo de Estudo</h3>
                        <div className="gpmantb-form-group">
                            <label>Nome do Grupo *</label>
                            <input
                                type="text"
                                value={groupName}
                                onChange={e => setGroupName(e.target.value)}
                                placeholder="Digite o nome do grupo"
                                maxLength={100}
                                autoFocus
                            />
                        </div>
                        <div className="gpmantb-form-group">
                            <label>Descrição (opcional)</label>
                            <textarea
                                value={groupDescription}
                                onChange={e => setGroupDescription(e.target.value)}
                                placeholder="Breve descrição do grupo..."
                                maxLength={500}
                                rows={3}
                            />
                        </div>
                        <div className="gpmantb-dialog-actions">
                            <button
                                className="gpmantb-btn-cancel"
                                onClick={() => { setShowCreateModal(false); setGroupName(''); setGroupDescription(''); }}
                            >
                                Cancelar
                            </button>
                            <button
                                className="gpmantb-btn-primary"
                                onClick={handleCreateGroup}
                                disabled={!groupName.trim()}
                            >
                                Criar Grupo
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Modal Upgrade ── */}
            {showUpgradeModal && <GroupUpgradeModal onClose={() => setShowUpgradeModal(false)} />}
        </div>
    );
};

export default GroupsMainTab;